import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, useNavigate } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { USER_URL } from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { UsersItemSchema } from "../data/UsersItemSchema";
import UserSubmitForm from "../forms/UserSubmitForm";
import { useStepper } from "../../../hooks/stepper/useStepper";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import { StepperConfig } from "../../../types/Components/Stepper/StepperConfig";
import api from "../../../utils/api";
import { clearUsersCache } from "../../../helpers/checkEmailExists";

const NewUser: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    trigger,
    resetField,
  } = useForm({
    resolver: zodResolver(UsersItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const onSubmit = async (data: FieldValues) => {
    // TODO:  Pretende receber Alertas do SISAN também por telemóvel? (data.allowReceiveAlerts)
    const bodyRequest = {
      name: data.name,
      email: data.email ? data.email : null,
      position: data.position ? data.position : "",
      organizationId: data.organizationOption
        ? data.organizationOption.id
        : null,
      phoneNumber: data.phone ? data.phone : "",
      allowReceiveAlerts: false,
      countyId: data.countyOption ? data.countyOption.id : null,
      provinceId: data.provinceOption ? data.provinceOption.id : null,
      partnerId: data.partnerOption ? data.partnerOption.id : null,
      roles: data.roleOption ? [data.roleOption.id] : null,
    };

    await handleSubmitItem(
      () => api.post(USER_URL, bodyRequest),
      addToast,
      setIsLoaded,
      "Create",
      "Utilizador criado",
    );
    navigate("/utilizadores");
  };

  useEffect(() => {
    return () => {
      clearUsersCache();
    };
  }, []);

  const steps = [
    { title: "Preencher as informações do utilizador", description: "Passo 1" },
    { title: "Verificar as informações do utilizador", description: "Passo 2" },
  ];

  const { currentStep, handleNext, handleBack, handleFinalSubmit } = useStepper(
    steps,
    trigger,
    handleSubmit,
    onSubmit,
  );

  const stepperConfig: StepperConfig = {
    nextButtonName: "Criar utilizador",
    steps,
    onNext: currentStep === steps.length - 1 ? handleFinalSubmit : handleNext,
    onBack: handleBack,
    isNextActive: true,
    isFinalStep: currentStep === steps.length - 1,
    currentStep,
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <SubmitItem
        title="Criar utilizador"
        hasAlert={currentStep === steps.length - 1}
        hasSubmitFooter
        hasStepper
        stepperConfig={stepperConfig}
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
        isDirty={isDirty}
      >
        <UserSubmitForm
          errors={errors}
          control={control}
          resetField={resetField}
          watch={watch}
          isDisabled={currentStep === steps.length - 1}
        />
      </SubmitItem>
    </Form>
  );
};

export default NewUser;
